import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Icon from '@components/Icon';
import { Button, Tooltip, Popover, Segmented, ConfigProvider, Popconfirm, Slider, InputNumber, Tabs, Radio } from 'antd';
import stores from '@stores';
import { toDownloadFile, nanoid, modKey } from '@utils/utils';
import useKeyboardShortcuts from '@hooks/useKeyboardShortcuts';

export default observer(() => {
    const { t } = useTranslation('toolbar');
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [format, setFormat] = useState('png');
    const [ratio, setRatio] = useState(1);
    const [canCopy, setCanCopy] = useState(true);
    const [customWidth, setCustomWidth] = useState(null);
    const [customHeight, setCustomHeight] = useState(null);
    const [resolutionMode, setResolutionMode] = useState('ratio'); // 'ratio' or 'custom'
    
    // 当选择不同的分辨率模式或比例变化时，更新自定义宽高
    useEffect(() => {
        if (stores.option.frameConf.width && resolutionMode === 'ratio') {
            setCustomWidth(Math.round(stores.option.frameConf.width * ratio));
            setCustomHeight(Math.round(stores.option.frameConf.height * ratio));
        }
    }, [ratio, resolutionMode, stores.option.frameConf.width, stores.option.frameConf.height]);
    
    // 检查浏览器是否支持Clipboard API
    useEffect(() => {
        const isClipboardSupported = !!navigator.clipboard && 
            typeof navigator.clipboard.write === 'function';
        setCanCopy(isClipboardSupported);
    }, []);
    
    const handleOpenChange = (newOpen) => {
        setOpen(newOpen);
    };
    
    // 维持宽高比例
    const handleWidthChange = (value) => {
        setCustomWidth(value);
        if (stores.option.frameConf.width && stores.option.frameConf.height) {
            const aspectRatio = stores.option.frameConf.width / stores.option.frameConf.height;
            setCustomHeight(Math.round(value / aspectRatio));
        }
    };
    
    const handleHeightChange = (value) => {
        setCustomHeight(value);
        if (stores.option.frameConf.width && stores.option.frameConf.height) {
            const aspectRatio = stores.option.frameConf.width / stores.option.frameConf.height;
            setCustomWidth(Math.round(value * aspectRatio));
        }
    };
    
    const toDownload = async () => {
        if (!stores.editor.isEditing) return;
        if (loading) return;
        
        // 准备导出选项
        const option = {};
        
        // 根据分辨率模式设置导出尺寸
        if (resolutionMode === 'ratio') {
            option.pixelRatio = ratio;
        } else if (resolutionMode === 'custom' && customWidth && customHeight) {
            option.width = customWidth;
            option.height = customHeight;
        }
        
        // 为jpg和webp格式添加填充颜色和质量选项
        if (['jpg', 'webp'].includes(format)) {
            option.quality = 0.9;
            option.fill = '#ffffff';
        }
        
        const key = nanoid();
        setLoading(true);
        
        // 显示加载提示
        stores.editor.message.open({
            key,
            type: 'loading',
            content: t('download.downloading'),
        });
        
        try {
            // 添加回调函数处理Canvas，以应用滤镜效果
            if (stores.option.enableFilter && stores.option.filterString) {
                option.callback = (canvas) => {
                    // 创建临时Canvas以保存原始图像
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = canvas.width;
                    tempCanvas.height = canvas.height;
                    const tempCtx = tempCanvas.getContext('2d');
                    
                    // 复制原始图像到临时Canvas
                    tempCtx.drawImage(canvas, 0, 0);
                    
                    // 在原始Canvas上应用滤镜
                    const ctx = canvas.getContext('2d');
                    ctx.filter = stores.option.filterString;
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(tempCanvas, 0, 0);
                    
                    // 重置滤镜，避免影响其他操作
                    ctx.filter = 'none';
                }
            }
            
            // 导出图像
            const result = await stores.editor.app.tree.export(format, option);
            
            // 下载文件
            let name = t('download.defaultFilename');
            if (resolutionMode === 'ratio' && ratio > 1) {
                name += `@${ratio}`;
            } else if (resolutionMode === 'custom') {
                name += `_${customWidth}x${customHeight}`;
            }
            toDownloadFile(result.data, `${name}.${format}`);
            
            // 显示成功消息
            stores.editor.message.open({
                key,
                type: 'success',
                content: t('download.downloadSuccess'),
            });
        } catch (error) {
            console.error(t('download.error.exportFailed'), error);
            
            // 显示错误消息
            stores.editor.message.open({
                key,
                type: 'error',
                content: t('download.downloadError'),
            });
        } finally {
            // 重置加载状态
            setLoading(false);
        }
    };
    
    const toCopy = async () => {
        if (!stores.editor.isEditing) return;
        if (loading) return;
        if (!canCopy) {
            stores.editor.message.info(t('download.copyNotSupported'));
            return;
        }
        
        const key = nanoid();
        setLoading(true);
        
        stores.editor.message.open({
            key,
            type: 'loading',
            content: t('download.copying'),
        });
        
        try {
            // 准备导出选项
            const option = { blob: true };
            
            // 根据分辨率模式设置导出尺寸
            if (resolutionMode === 'ratio') {
                option.pixelRatio = ratio;
            } else if (resolutionMode === 'custom' && customWidth && customHeight) {
                option.width = customWidth;
                option.height = customHeight;
            }
            
            // 添加回调函数处理Canvas，以应用滤镜效果
            if (stores.option.enableFilter && stores.option.filterString) {
                option.callback = (canvas) => {
                    // 创建临时Canvas以保存原始图像
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = canvas.width;
                    tempCanvas.height = canvas.height;
                    const tempCtx = tempCanvas.getContext('2d');
                    
                    // 复制原始图像到临时Canvas
                    tempCtx.drawImage(canvas, 0, 0);
                    
                    // 在原始Canvas上应用滤镜
                    const ctx = canvas.getContext('2d');
                    ctx.filter = stores.option.filterString;
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(tempCanvas, 0, 0);
                    
                    // 重置滤镜，避免影响其他操作
                    ctx.filter = 'none';
                }
            }
            
            const result = await stores.editor.app.tree.export('png', option);
            const { data } = result;
            
            await navigator.clipboard.write([
                new ClipboardItem({
                    [data.type]: data,
                }),
            ]);
            
            stores.editor.message.open({
                key,
                type: 'success',
                content: t('download.copySuccess'),
            });
        } catch (error) {
            console.error(t('download.error.copyFailed'), error);
            
            stores.editor.message.open({
                key,
                type: 'error',
                content: t('download.copyError'),
            });
        } finally {
            setLoading(false);
        }
    }
    
    const confirm = () => {
        stores.editor.destroy();
        stores.editor.clearImg();
        stores.editor.clearFun && stores.editor.clearFun();
    }
    
    useKeyboardShortcuts(() => toDownload(), () => toCopy(), [toDownload, toCopy]);
    
    // 展示当前的下载尺寸信息
    const getDownloadSizeInfo = () => {
        if (!stores.option.frameConf.width) return null;
        
        if (resolutionMode === 'ratio') {
            return (
                <div className="text-xs p-2.5 mt-4 flex justify-between bg-black/5 rounded-md">
                    <span className="text-gray-500">{t('download.downloadSize')}</span>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{Math.round(stores.option.frameConf.width * ratio)} x {Math.round(stores.option.frameConf.height * ratio)}</span>
                </div>
            );
        } else {
            return (
                <div className="text-xs p-2.5 mt-4 flex justify-between bg-black/5 rounded-md">
                    <span className="text-gray-500">{t('download.downloadSize')}</span>
                    <span className="text-gray-700 dark:text-gray-300 font-medium">{customWidth} x {customHeight}</span>
                </div>
            );
        }
    };
    
    const formatTabItems = [
        {
            key: 'basic',
            label: t('download.basicFormat'),
            children: (
                <Radio.Group 
                    className="w-full flex justify-between" 
                    value={format} 
                    onChange={(e) => setFormat(e.target.value)}
                >
                    <Radio.Button value="png" className="flex-1 text-center text-xs">PNG</Radio.Button>
                    <Radio.Button value="jpg" className="flex-1 text-center text-xs">JPG</Radio.Button>
                    <Radio.Button value="webp" className="flex-1 text-center text-xs">WEBP</Radio.Button>
                    <Radio.Button value="bmp" className="flex-1 text-center text-xs">BMP</Radio.Button>
                </Radio.Group>
            )
        }
    ];
    
    const resolutionTabItems = [
        {
            key: 'ratio',
            label: t('download.defaultRatio'),
            children: (
                <>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">{t('download.scaleRatio')}</span>
                        <InputNumber
                            min={0.5}
                            max={5}
                            step={0.5}
                            value={ratio}
                            onChange={setRatio}
                            size="small"
                            className="w-20 text-xs"
                            addonAfter="x"
                        />
                    </div>
                    <Slider
                        min={0.5}
                        max={5}
                        step={0.5}
                        value={ratio}
                        onChange={setRatio}
                        marks={{
                            0.5: <span className="text-xs">0.5x</span>,
                            1: <span className="text-xs">1x</span>,
                            2: <span className="text-xs">2x</span>,
                            3: <span className="text-xs">3x</span>,
                            4: <span className="text-xs">4x</span>,
                            5: <span className="text-xs">5x</span>
                        }}
                        tooltip={{
                            formatter: (value) => `${value}x`
                        }}
                    />
                </>
            )
        },
        {
            key: 'custom',
            label: t('download.customSize'),
            children: (
                <>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">{t('download.width')}</span>
                        <InputNumber
                            min={50}
                            max={10000}
                            value={customWidth}
                            onChange={handleWidthChange}
                            size="small"
                            className="w-24 text-xs"
                            addonAfter="px"
                        />
                    </div>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">{t('download.height')}</span>
                        <InputNumber
                            min={50}
                            max={10000}
                            value={customHeight}
                            onChange={handleHeightChange}
                            size="small"
                            className="w-24 text-xs"
                            addonAfter="px"
                        />
                    </div>
                </>
            )
        }
    ];
    
    const content = (
        <div className="p-2">
            <ConfigProvider
                theme={{
                    components: {
                        Tabs: {
                            fontSize: 12,
                            itemActiveColor: '#7c3aed',
                            itemSelectedColor: '#7c3aed',
                            itemHoverColor: '#7c3aed',
                        },
                        Radio: {
                            buttonFontSize: 11,
                        },
                        Slider: {
                            handleSize: 14,
                            railSize: 4,
                            trackBg: '#7c3aed',
                            handleColor: '#7c3aed',
                        },
                        InputNumber: {
                            fontSize: 12,
                        },
                    },
                }}
            >
                <div className="mb-4">
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">{t('download.imageFormat')}</div>
                    <Tabs items={formatTabItems} size="small" />
                </div>
                
                <div className="mb-2">
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">{t('download.resolutionSettings')}</div>
                    <Tabs 
                        items={resolutionTabItems} 
                        activeKey={resolutionMode}
                        onChange={setResolutionMode}
                        size="small"
                    />
                </div>
            </ConfigProvider>
            
            {getDownloadSizeInfo()}
            
            <div className="mt-6 flex gap-2">
            <ConfigProvider
                theme={{
                    components: {
                        Button: {
                                colorPrimary: stores.editor.isDark ? '#7c3aed' : '#7c3aed',
                                borderRadius: 8,
                                algorithm: true,
                            },
                    },
                }}
            >
                        <Button
                            type='primary'
                        size='middle'
                        block
                            loading={loading}
                        icon={<Icon.ImageDown size={14} className="mr-1" />}
                            onClick={toDownload}
                        className="py-2.5 flex items-center justify-center h-auto text-sm font-medium"
                    >
                        {t('download.downloadImage', { format: format.toUpperCase() })}
                        </Button>
                    
                        <Button
                        size='middle'
                        icon={<Icon.Copy size={14} className="mr-1" />}
                            loading={loading}
                            onClick={toCopy}
                            disabled={!canCopy}
                        className="py-2.5 flex items-center justify-center h-auto text-sm font-medium"
                    >
                        {t('download.copyToClipboard')}
                    </Button>
                </ConfigProvider>
            </div>
                </div>
    );
            
    return (
        <div className='shrink-0 py-4 px-6 flex gap-2 justify-center items-center'>
            <div className="flex items-center gap-1">
                <Popover
                    content={content}
                    trigger='click'
                    arrow={false}
                    placement="topRight"
                    open={open}
                    overlayStyle={{
                        width: '350px',
                    }}
                    onOpenChange={handleOpenChange}
                >
                    <Button 
                        size='middle' 
                        type="primary"
                        className="bg-purple-600 hover:bg-purple-700 text-sm font-medium"
                        icon={<Icon.ImageDown size={16} className="mr-1" />} 
                    >
                        {t('download.title')}
                    </Button>
                </Popover>
                {stores.editor.img?.src && 
                    <Popconfirm
                        title={t('download.deleteConfirm.title')}
                        description={t('download.deleteConfirm.description')}
                        placement="topRight"
                        onConfirm={confirm}
                        okText={t('download.deleteConfirm.okText')}
                        cancelText={t('download.deleteConfirm.cancelText')}
                    >
                        <Button 
                            size='middle' 
                            type="primary"
                            danger
                            className="text-sm font-medium"
                            icon={<Icon.Trash2 size={16} className="mr-1" />}
                        >
                            {t('download.deleteImage')}
                        </Button>
                    </Popconfirm>
                }
            </div>
        </div>
    );
});
